import { useSubscriptionsStore } from "@shared/stores/subscriptions.ts";
import { watch } from "vue";
import { cnameService } from "@shared/services/cnameService.ts";
import { useDnsRoutingStore } from "@shared/stores/dnsRouting.ts";
import { useDnsRoutingHelper } from "@shared/use/dnsRoutingHelper.ts";
import { SubscriptionDTO } from "@shared/types/common";
import { useTokenGeneration } from "@shared/use/useTokenGeneration.ts";
import { featureFlagService } from "@shared/services/featureFlag.ts";
import { useCnameStore } from "@shared/stores/cname.ts";
import { useFeaturesStore } from "@shared/stores/features.ts";
import { useTokenGenerationStore } from "@shared/stores/tokenGeneration.ts";
import { usageExplorer } from "@shared/services/usageExplorer.ts";
import logger from "@shared/utils/logger";
import { UsageExplorerFeatureFlagName } from "@shared/types/localTypes/usage-explorer.ts";

const STORAGE_BREAKDOWN_FLAG: UsageExplorerFeatureFlagName =
  "storage_breakdown";

export const useFeatureFlagHelper = () => {
  const subscriptionStore = useSubscriptionsStore();
  const dnsRoutingStore = useDnsRoutingStore();
  const cnameStore = useCnameStore();
  const featuresStore = useFeaturesStore();
  const tokenGenerationStore = useTokenGenerationStore();

  const dnsRoutingHelper = useDnsRoutingHelper();

  const loadDnsRoutingPolicies = async (
    currentSubscription: SubscriptionDTO,
  ) => {
    dnsRoutingStore.setIsLoadingPolicies(true);
    await dnsRoutingHelper.fetchRoutingPolicies(currentSubscription);
    dnsRoutingStore.setIsLoadingPolicies(false);
  };

  const loadUsageExplorerFeatureFlags = async (subscriptionNumber: number) => {
    let enabled = false;
    try {
      const response = await usageExplorer.fetchUsageExplorerFeatureFlags({
        subscription: subscriptionNumber,
      });
      enabled = response.feature_flags.includes(STORAGE_BREAKDOWN_FLAG);
    } catch (e) {
      logger.warn(e);
    }
    if (
      Number(subscriptionStore.currentSubscription?.accountNumber) !==
      subscriptionNumber
    ) {
      return;
    }
    featuresStore.setIsStorageBreakdownEnabled(enabled);
  };

  watch(
    () => subscriptionStore.currentSubscription,
    (currentSubscription) => {
      if (!currentSubscription) {
        return;
      }
      featureFlagService.loadFeatureFlags({
        subscriptionNumber: currentSubscription.accountNumber,
      });

      cnameService.getSslList(currentSubscription.accountNumber);

      loadDnsRoutingPolicies(currentSubscription);

      loadUsageExplorerFeatureFlags(currentSubscription.accountNumber);

      useTokenGeneration().getApiToken(currentSubscription);
    },
    {
      immediate: true,
    },
  );

  watch(
    () => cnameStore.sslList,
    (sslList) => {
      featuresStore.setIsCnameEnabled(!!sslList.length);
    },
  );

  watch(
    () => dnsRoutingStore.policiesResponse,
    (policiesResponse) => {
      featuresStore.setIsDnsManualFailoverPolicyEnabled(
        policiesResponse.policies.some(
          (policy) => policy.policy_type === "manual_failover",
        ),
      );
    },
  );

  watch(
    () => tokenGenerationStore.apiKey,
    (apiKey) => {
      featuresStore.setIsSaasTokenEnabled(!!apiKey.length);
    },
  );
};
